import React, { CSSProperties, useMemo, useRef, useState } from 'react';
import type { StrictModifiers } from '@popperjs/core';
import { BackBoldArrowIcon } from '../../assets/icons';
import DropdownListPanel from '../Dropdown/DropdownListPanel';
import { DropdownEmpty } from '../Dropdown/DropdownStyled';
import type { DropdownItem } from '../Dropdown';
import { useClickOutside } from '../Dropdown/useClickOutside';
import * as S from './SearchableDropdownStyled';

export type SearchableDropdownItem = DropdownItem;

export type SearchableDropdownProps = {
  value: string;
  options: readonly SearchableDropdownItem[];
  error?: boolean;
  errorText?: string;
  placeholder?: string;
  inputStyle?: CSSProperties;
  emptyText?: string;
  onChange: (value: string) => void;
  dataTestId?: string;
  filterOption?: (option: SearchableDropdownItem, query: string) => boolean;
  onBlur?: () => void;
};

const defaultFilterOption = (option: SearchableDropdownItem, query: string) =>
  option.label.toLowerCase().includes(query.toLowerCase());

const searchableDropdownModifiers: StrictModifiers[] = [
  {
    name: 'preventOverflow',
    options: {
      padding: 8,
      altAxis: true,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 8,
    },
  },
];

const SearchableDropdown = ({
  value,
  onChange,
  options,
  onBlur,
  error = false,
  errorText = '',
  placeholder,
  filterOption,
  inputStyle,
  emptyText = 'Ничего не найдено',
  dataTestId,
}: SearchableDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const [fieldElement, setFieldElement] = useState<HTMLDivElement | null>(null);

  const handleFieldRef = (node: HTMLDivElement | null) => {
    fieldRef.current = node;
    setFieldElement(node);
  };

  const filteredOptions = useMemo(() => {
    const query = value.trim();
    if (!query) return options;

    return options.filter((option) =>
      filterOption ? filterOption(option, query) : defaultFilterOption(option, query),
    );
  }, [filterOption, options, value]);

  useClickOutside({
    isActive: isOpen,
    containerRef: fieldRef,
    onOutside: () => setIsOpen(false),
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
    setIsOpen(true);
  };

  const handleSelect = (option: SearchableDropdownItem) => {
    onChange(option.label);
    setIsOpen(false);
  };

  const showEmptyState = value.trim().length > 0 && filteredOptions.length === 0;

  return (
    <S.SearchableDropdownWrap>
      <S.SearchableDropdownField ref={handleFieldRef} $hasError={error} style={inputStyle}>
        <S.SearchableDropdownInput
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => onBlur?.()}
          placeholder={placeholder}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          data-testid={dataTestId}
        />
        <S.SearchableDropdownToggle
          type="button"
          aria-label="Открыть список"
          aria-expanded={isOpen}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <S.SearchableDropdownArrow $open={isOpen}>
            <BackBoldArrowIcon />
          </S.SearchableDropdownArrow>
        </S.SearchableDropdownToggle>
      </S.SearchableDropdownField>
      {errorText && <S.SearchableDropdownError>{errorText}</S.SearchableDropdownError>}
      <DropdownListPanel
        open={isOpen && (filteredOptions.length > 0 || showEmptyState)}
        targetRef={fieldElement || undefined}
        width={fieldElement?.offsetWidth || 528}
        options={filteredOptions}
        panelTestId={dataTestId ? `${dataTestId}-panel` : undefined}
        optionTestIdPrefix={dataTestId ? `${dataTestId}-option` : undefined}
        onSelect={handleSelect}
        modifiers={searchableDropdownModifiers}
        optionBold={false}
        onOptionMouseDown={(event) => event.preventDefault()}
        emptyContent={showEmptyState ? <DropdownEmpty>{emptyText}</DropdownEmpty> : null}
      />
    </S.SearchableDropdownWrap>
  );
};

export default SearchableDropdown;
